import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';

import {
	handleError,
	log
} from '../../../../services/utils';

import { downloadAsset } from '../../../../api/nora/assets';
import {
	getProgramList,
	deleteProgram,
	exportProgramList
} from '../../../../api/nora/programs';

import { useAuthState } from '../../../../contexts/AuthContext';
import {
	useProgramState,
	useProgramAction
} from '../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../contexts/UIContext';

import Alert from '../../../Alert';
import DateCell from '../../../Table/Cells/DateCell';
import HelpModal from '../../../Shared/HelpModal';
import MultiSelectColumnFilter from '../../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../../Table/filterFunctions/multiSelectFilterFn';
import PageHeader from '../../../Shared/PageHeader';
import Table from '../../../Table';
import TableRowActions from '../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../Table/filterFunctions/multiWordFilterFn';

const ProgramList = () => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const {
		initialized,
		programList,
		tableState
	} = useProgramState();
	const {
		clearProgram,
		setProgramList,
		setTableState
	} = useProgramAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ programToDelete, setProgramToDelete ] = useState( {} );

	useEffect( () => {
		setInfobar( <span>Operations &nbsp;&#9656;&nbsp; Programs</span> );
	}, [ setInfobar ] );

	const retrieveProgramList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Program List...' );

		try {
			clearProgram();
			const data = await getProgramList();

			setProgramList( data );
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}

		setInfobarLoader( '' );
	}, [
		clearProgram,
		setInfobarLoader,
		setProgramList,
		setApiErrorAlert
	] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait... ' );
			} else {
				if ( permissions.listPrograms.canExecute && permissions.listPrograms.isAvailable ) {
					try {
						await retrieveProgramList();
						setInfobarLoader( '' );
					} catch ( error ) {
						if ( error.message !== 'canceled' ) {
							handleError( error );
							setInfobarLoader( '' );
						}
					}
				} else {
					setInfobarLoader( '' );
				}

				setIsLoading( false );
			}
		} )();
	}, [
		initialized,
		permissions,
		retrieveProgramList,
		setInfobarLoader
	] );

	const startDateCell = useCallback( ( { row } ) => <DateCell date={ row.original.startDate } />, [] );

	const endDateCell = useCallback( ( { row } ) => <DateCell date={ row.original.endDate } />, [] );

	const onView = useCallback( ( { id } ) => {
		history.push( `/operations/programs/${ id }` );
	}, [ history ] );

	const onDelete = useCallback( program => {
		setProgramToDelete( program );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewPrograms.canExecute,
				icon: 'search',
				permission: permissions.viewPrograms.isAvailable,
				text: 'View Program'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.deletePrograms.canExecute,
				icon: 'times',
				permission: permissions.deletePrograms.isAvailable,
				text: 'Delete Program'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Program Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'type',
			accessor: row => `${ row.type }`,
			Header: 'Program Type',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			accessor: 'startDate',
			Header: 'Start Date',
			filter: multiWordFilterFn,
			Cell: startDateCell,
			minWidth: 60
		},
		{
			accessor: 'endDate',
			Header: 'End Date',
			filter: multiWordFilterFn,
			Cell: endDateCell,
			minWidth: 60
		},
		{
			id: 'status',
			accessor: row => `${ row.submissionStatus }`,
			Header: 'Status',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		endDateCell,
		startDateCell,
		tableRowActionsCell
	] );

	const addProgram = useCallback( () => {
		history.push( '/operations/programs/0' );
	}, [ history ] );

	const exportAll = useCallback( async () => {
		setScreenLoader( 'Exporting programs, Please wait...' );

		try {
			const guid = await exportProgramList( null );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader
	] );

	const exportFiltered = useCallback( async () => {
		setScreenLoader( 'Exporting programs, Please wait...' );

		try {
			const ids = tableState.rows.map( row => row.original.id );

			const guid = await exportProgramList( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		tableState.rows
	] );

	const exportSelected = useCallback( async () => {
		setScreenLoader( 'Exporting programs, Please wait...' );

		try {
			const ids = tableState.selectedIds;

			const guid = await exportProgramList( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		tableState.selectedIds
	] );

	const actions = useMemo( () => [
		{
			action: {
				'All Rows': exportAll,
				...tableState.filters.length
					? { 'Filtered Rows': exportFiltered }
					: {},
				...tableState.selectedIds.length
					? { 'Selected Rows': exportSelected }
					: {}
			},
			disable: !permissions.listPrograms.canExecute,
			dropdown: true,
			helpId: 4,
			icon: 'cog',
			permission: permissions.listPrograms.isAvailable,
			text: 'Export CSV'
		},
		{
			action: addProgram,
			disable: !permissions.addPrograms.canExecute,
			icon: 'add',
			permission: permissions.addPrograms.isAvailable,
			text: 'Add Program'
		}
	], [
		addProgram,
		exportAll,
		exportFiltered,
		exportSelected,
		permissions,
		tableState.filters.length,
		tableState.selectedIds.length
	] );

	const handleDeleteProgram = useCallback( async () => {
		try {
			await deleteProgram( programToDelete.id );
			await retrieveProgramList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		programToDelete.id,
		retrieveProgramList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to delete <strong>{ programToDelete.programName }</strong> from the program list?</span>, [ programToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="page">
			{ log( 'programList' ) }
			<PageHeader
				actions={ actions }
				title={
					<>
						<span>Program List</span>
						<HelpModal
							id={ 8 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<Table
				columns={ columns }
				data={ programList }
				hasPermission={ permissions.listPrograms.isAvailable }
				selectable
				setTableState={ setTableState }
				tableState={ tableState }
			/>
			<Alert
				action={ handleDeleteProgram }
				name="program-list-delete-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</div>
	);
};

export default memo( ProgramList );
